import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, ArrowLeft, CheckCircle2, PlayCircle, X, TrendingUp, Sparkles } from "lucide-react";
import { VideoPlayer } from "@/components/VideoPlayer";
import { WorkoutPlayer } from "@/components/WorkoutPlayer";

export default function ClientWorkouts() {
  const [tab, setTab] = useState("programs");
  const [programs, setPrograms] = useState([]);
  const [onDemand, setOnDemand] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [logs, setLogs] = useState([]);
  const [active, setActive] = useState(null);
  const [week, setWeek] = useState(1);
  const [playing, setPlaying] = useState(null);
  const [libVideo, setLibVideo] = useState(null);
  const [progressEx, setProgressEx] = useState("");

  const loadLogs = useCallback(() => api.get("/workouts/log").then((r) => setLogs(r.data)).catch(() => {}), []);
  const loadPrograms = useCallback(() => api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {}), []);
  useEffect(() => {
    loadPrograms();
    api.get("/programs/on-demand").then((r) => setOnDemand(r.data)).catch(() => {});
    api.get("/exercises").then((r) => setExercises(r.data)).catch(() => {});
    loadLogs();
  }, [loadLogs, loadPrograms]);

  const startProgram = async (p) => {
    try {
      await api.post(`/programs/${p.program_id}/start`);
      toast.success(`${p.title} added to your programs`);
      loadPrograms();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exById = useMemo(() => Object.fromEntries(exercises.map((e) => [e.exercise_id, e])), [exercises]);
  const availableOnDemand = useMemo(
    () => onDemand.filter((p) => !programs.some((mine) => mine.program_id === p.program_id)),
    [onDemand, programs]
  );
  const logsWithSummary = useMemo(() => logs.map((l) => ({
    ...l,
    exercise_summary: [...new Set((l.sets_completed || []).map((s) => s.exercise_name))].filter(Boolean).join(" · "),
  })), [logs]);
  const isDone = (programId, w, d) => logs.some((l) => l.program_id === programId && l.week === w && l.day === d && l.completed !== false);

  const loggedExercises = useMemo(() => {
    const seen = new Map();
    for (const l of logs) for (const s of l.sets_completed || []) {
      if (s.exercise_id && !seen.has(s.exercise_id)) seen.set(s.exercise_id, s.exercise_name || exById[s.exercise_id]?.name || "Exercise");
    }
    return [...seen.entries()];
  }, [logs, exById]);

  const progressRows = useMemo(() => {
    if (!progressEx) return [];
    return logs
      .map((l) => {
        const sets = (l.sets_completed || []).filter((s) => s.exercise_id === progressEx);
        if (!sets.length) return null;
        const top = sets.reduce((a, b) => (parseFloat(b.weight || 0) > parseFloat(a.weight || 0) ? b : a), sets[0]);
        const volume = sets.reduce((sum, s) => sum + (parseFloat(s.reps || 0) * parseFloat(s.weight || 0)), 0);
        return { date: l.date, top, volume, setCount: sets.length };
      })
      .filter(Boolean);
  }, [logs, progressEx]);

  if (playing) {
    return (
      <WorkoutPlayer program={active} workout={playing} exercisesById={exById}
        onBack={() => setPlaying(null)}
        onCompleted={() => { setPlaying(null); loadLogs(); }} />
    );
  }

  if (active) {
    const weekWorkouts = (active.workouts || []).filter((w) => w.week === week).sort((a, b) => a.day - b.day);
    return (
      <div className="space-y-8 lg:mt-0 mt-10" data-testid="client-program-detail">
        <button className="btn-ghost" onClick={() => setActive(null)} data-testid="back-programs"><ArrowLeft size={14} /> Back</button>
        <header>
          <div className="overline">{active.program_type} · {active.duration_weeks} weeks</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3">{active.title}</h1>
          <p className="text-secondary-luxe mt-3 text-sm max-w-2xl">{active.description}</p>
        </header>
        <div className="flex gap-2 flex-wrap">
          {Array.from({ length: active.duration_weeks }, (_, i) => i + 1).map((w) => (
            <button key={w} onClick={() => setWeek(w)} data-testid={`client-week-tab-${w}`}
              className={w === week ? "btn-primary text-xs" : "btn-secondary text-xs"}>Week {w}</button>
          ))}
        </div>
        {weekWorkouts.length === 0 ? (
          <div className="card-luxe">
            <div className="overline">Note</div>
            <p className="mt-3 text-secondary-luxe text-sm">No training scheduled this week yet — your coach is preparing it.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {weekWorkouts.map((wo) => {
              const done = isDone(active.program_id, wo.week, wo.day);
              return (
                <div key={`${wo.week}-${wo.day}`} className="card-luxe" data-testid={`workout-day-${wo.day}`}>
                  <div className="flex items-center justify-between">
                    <div className="overline">Day {wo.day}</div>
                    {done && <span className="flex items-center gap-1 text-xs" style={{ color: "var(--accent)" }} data-testid={`done-badge-${wo.day}`}><CheckCircle2 size={13} /> Completed</span>}
                  </div>
                  <h3 className="font-serif text-2xl mt-2">{wo.name}</h3>
                  <ul className="mt-3 space-y-1 text-sm text-secondary-luxe">
                    {(wo.exercises || []).map((ex, i) => (
                      <li key={`${ex.exercise_id || ex.name}-${i}`} className="flex items-center gap-2">
                        {exById[ex.exercise_id]?.video_url && <PlayCircle size={12} style={{ color: "var(--accent)" }} />}
                        {ex.name} — {ex.sets}×{ex.reps}
                      </li>
                    ))}
                  </ul>
                  <button className="btn-primary mt-5" onClick={() => setPlaying(wo)} data-testid={`start-workout-${wo.day}`}>
                    {done ? "Repeat workout" : "Start workout"} <ChevronRight size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8 lg:mt-0 mt-10" data-testid="client-workouts">
      <header>
        <div className="overline">Training</div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3">Your training</h1>
      </header>

      <div className="flex gap-2 flex-wrap">
        {[["programs", "Programs"], ["history", "History"], ["library", "Exercise library"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
            className={tab === key ? "btn-primary text-xs" : "btn-secondary text-xs"}>{label}</button>
        ))}
      </div>

      {tab === "programs" && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {programs.map((p) => (
            <button key={p.program_id} onClick={() => { setActive(p); setWeek(1); }} className="card-luxe text-left block hover:border-luxe-strong" data-testid={`client-program-${p.program_id}`}>
              {p.cover_url && <img src={p.cover_url} alt="" className="w-full h-36 object-cover -mx-6 -mt-6 mb-4" style={{ width: "calc(100% + 3rem)" }} />}
              <div className="overline">{p.program_type}</div>
              <div className="font-serif text-2xl mt-2">{p.title}</div>
              <p className="text-secondary-luxe text-sm mt-2 line-clamp-3">{p.description}</p>
              <div className="mt-4 flex items-center justify-between text-xs text-secondary-luxe">
                <span>{p.duration_weeks} weeks · {(p.workouts || []).length} workouts</span>
                <ChevronRight size={14} />
              </div>
            </button>
          ))}
          {programs.length === 0 && <div className="text-secondary-luxe">No programs assigned yet — start one from the on-demand library below, or your coach is preparing yours.</div>}
        </div>

        {availableOnDemand.length > 0 && (
          <section className="mt-10" data-testid="ondemand-library">
            <div className="overline flex items-center gap-2"><Sparkles size={12} /> On-demand library</div>
            <p className="text-secondary-luxe text-sm mt-2">Ready-made programs you can start anytime.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
              {availableOnDemand.map((p) => (
                <div key={p.program_id} className="card-luxe" data-testid={`ondemand-program-${p.program_id}`}>
                  {p.cover_url && <img src={p.cover_url} alt="" className="w-full h-36 object-cover -mx-6 -mt-6 mb-4" style={{ width: "calc(100% + 3rem)" }} />}
                  <div className="overline">{p.program_type}</div>
                  <div className="font-serif text-2xl mt-2">{p.title}</div>
                  <p className="text-secondary-luxe text-sm mt-2 line-clamp-3">{p.description}</p>
                  <div className="mt-3 text-xs text-secondary-luxe">{p.duration_weeks} weeks · {(p.workouts || []).length} workouts</div>
                  <button className="btn-primary mt-4" onClick={() => startProgram(p)} data-testid={`start-ondemand-${p.program_id}`}>
                    Start program <ChevronRight size={13} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
        </>
      )}

      {tab === "history" && (
        <div className="space-y-8" data-testid="history-panel">
          {loggedExercises.length > 0 && (
            <div className="card-luxe">
              <div className="overline flex items-center gap-2"><TrendingUp size={12} /> Strength progress</div>
              <select className="input-luxe max-w-sm mt-4" value={progressEx} onChange={(e) => setProgressEx(e.target.value)} data-testid="progress-exercise-select">
                <option value="">Choose an exercise…</option>
                {loggedExercises.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
              {progressEx && (
                <div className="mt-5 space-y-2" data-testid="progress-rows">
                  {progressRows.map((r, i) => (
                    <div key={`${r.date}-${i}`} className="flex items-center justify-between text-sm py-2" style={{ borderBottom: "1px solid var(--border)" }}>
                      <span className="text-tertiary-luxe text-xs">{r.date}</span>
                      <span>Top set: {r.top.reps || "—"} reps {r.top.weight ? `@ ${r.top.weight} lb` : ""}</span>
                      <span className="text-secondary-luxe text-xs">{r.setCount} sets · {Math.round(r.volume)} lb volume</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="space-y-4">
            {logsWithSummary.map((l) => (
              <div key={l.workout_log_id} className="card-luxe" data-testid={`log-${l.workout_log_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="overline">{l.date} · Week {l.week}, Day {l.day}</div>
                    <h3 className="font-serif text-xl mt-1">{l.workout_name || "Workout"}</h3>
                  </div>
                  <div className="text-right text-xs text-secondary-luxe">
                    {l.duration_minutes ? <div>{l.duration_minutes} min</div> : null}
                    {l.rpe ? <div>RPE {l.rpe}/10</div> : null}
                  </div>
                </div>
                {l.exercise_summary && (
                  <div className="text-xs text-secondary-luxe mt-3">{l.exercise_summary}</div>
                )}
                {l.notes && <p className="text-sm text-secondary-luxe mt-2 italic">"{l.notes}"</p>}
              </div>
            ))}
            {logs.length === 0 && <div className="text-secondary-luxe">No workouts logged yet — your history will appear here.</div>}
          </div>
        </div>
      )}

      {tab === "library" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {exercises.map((ex) => (
            <button key={ex.exercise_id} className="card-luxe text-left hover:border-luxe-strong" onClick={() => setLibVideo(ex)} data-testid={`lib-exercise-${ex.exercise_id}`}>
              <div className="flex items-center justify-between">
                <div className="overline">{ex.category}</div>
                {ex.video_url && <PlayCircle size={14} style={{ color: "var(--accent)" }} />}
              </div>
              <div className="font-serif text-xl mt-2">{ex.name}</div>
              <div className="text-xs text-secondary-luxe mt-2">{(ex.muscle_groups || []).join(" · ")}</div>
              {ex.instructions && <p className="text-sm text-secondary-luxe mt-2 line-clamp-3">{ex.instructions}</p>}
            </button>
          ))}
        </div>
      )}

      {libVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(10,9,8,0.72)" }} onClick={() => setLibVideo(null)} data-testid="lib-video-modal">
          <div className="card-luxe max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">{libVideo.category}</div>
                <h2 className="font-serif text-3xl mt-2">{libVideo.name}</h2>
              </div>
              <button className="btn-ghost" onClick={() => setLibVideo(null)} data-testid="close-lib-modal"><X size={16} /></button>
            </div>
            {libVideo.video_url ? <div className="mt-5"><VideoPlayer url={libVideo.video_url} /></div> : <p className="text-sm text-tertiary-luxe mt-5">No demo video yet.</p>}
            {libVideo.instructions && <p className="text-sm text-secondary-luxe mt-4 whitespace-pre-wrap">{libVideo.instructions}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
